import { DecisionSupportRecommendations } from '@/components/decisionSupport/DecisionSupportRecommendations';
import {
    DecisionSupportSelection,
    NormalizedSlotResult,
    RecommendationRow,
    buildRecommendedColumn,
    recommendationSearchText,
    useAxleRecommended,
} from '@/components/decisionSupport/utils/decisionSupport.utils';
import { SURFACE_GRAY, TEXT_GRAY } from '@/constants/colors';
import { Box, Button, DialogActions, Typography } from '@mui/material';
import { FC, useCallback, useMemo, useState } from 'react';

import { AxleChip } from '../displayFields/chips';

type DecisionSupportSelectionContentProps = {
    onClose(): void;
    // Pre-normalized recommendation rows + server-driven columns (from the
    // shared `normalizeVisitReassignment*` normalizers).
    normalized: NormalizedSlotResult;
    // The currently-committed selection (the value the surface holds).
    selection: DecisionSupportSelection | null;
    onConfirm(selection: DecisionSupportSelection | null): void;
    // Header for the leading "name" column ("Candidate" for clinicians, "Date" for dates).
    candidateHeader: string;
    // Plural noun for the items in the list ("clinicians", "dates").
    itemNoun: string;
    // Whether to show the autocomplete search above the recommendations table.
    showRecommendationSearch?: boolean;
};

/**
 * Shared body for the decision-support selection dialogs. Renders the
 * Recommended / Not Recommended table from a `NormalizedSlotResult`, tracks a
 * draft selection, and confirms it on the action bar. Data-source-agnostic: the
 * legacy modern clinician dialog and the new `visitAssignment` clinician/date
 * dialogs all feed it the same normalized shape.
 */
export const DecisionSupportSelectionContent: FC<DecisionSupportSelectionContentProps> = ({
    onClose,
    normalized,
    selection,
    onConfirm,
    candidateHeader,
    itemNoun,
    showRecommendationSearch = true,
}) => {
    const { recommended, notRecommended, recommendedColumns, notRecommendedColumns } = normalized;

    const axleRecommended = useAxleRecommended(recommended);

    const candidateColumn = useMemo(
        () =>
            buildRecommendedColumn<RecommendationRow>({
                id: 'candidate',
                header: candidateHeader,
                getLabel: (row) => row.label,
                isRecommended: (row) => row.id === axleRecommended?.id,
            }),
        [axleRecommended, candidateHeader],
    );

    const recColumnsWithName = useMemo(
        () => [candidateColumn, ...recommendedColumns],
        [candidateColumn, recommendedColumns],
    );

    const notRecColumnsWithName = useMemo(
        () => [candidateColumn, ...notRecommendedColumns],
        [candidateColumn, notRecommendedColumns],
    );

    const [draftSelection, setDraftSelection] = useState<DecisionSupportSelection | null>(
        selection,
    );

    const handleRowSelect = useCallback((row: RecommendationRow) => {
        setDraftSelection({ id: row.id, label: row.label });
    }, []);

    const getOptionLabel = useCallback((row: RecommendationRow) => row.label, []);

    const isUnchanged = draftSelection?.id === selection?.id;

    const handleConfirm = useCallback(() => {
        if (!isUnchanged) {
            onConfirm(draftSelection);
        }
        onClose();
    }, [draftSelection, isUnchanged, onConfirm, onClose]);

    const isFromIncompatible = useMemo(
        () => !!draftSelection && notRecommended.some((row) => row.id === draftSelection.id),
        [notRecommended, draftSelection],
    );

    const summaryLabel = isUnchanged ? 'Current:' : 'Selection:';
    const summaryName = draftSelection?.label ?? 'None';
    const tabChipLabel = draftSelection
        ? isFromIncompatible
            ? 'Not Recommended'
            : 'Recommended'
        : null;

    return (
        <>
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    p: 2,
                    minHeight: 0,
                }}
            >
                <DecisionSupportRecommendations
                    recommended={recommended}
                    notRecommended={notRecommended}
                    recommendedColumns={recColumnsWithName}
                    notRecommendedColumns={notRecColumnsWithName}
                    getRowValue={(row: RecommendationRow) => row.id}
                    selectedValue={draftSelection?.id ?? null}
                    onRowSelect={handleRowSelect}
                    itemNoun={itemNoun}
                    getOptionLabel={showRecommendationSearch ? getOptionLabel : undefined}
                    getSearchText={showRecommendationSearch ? recommendationSearchText : undefined}
                />
            </Box>
            <DialogActions
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    p: 2,
                    backgroundColor: SURFACE_GRAY,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                    <Typography variant='body2' fontSize='14px' lineHeight='20px' color={TEXT_GRAY}>
                        {summaryLabel}
                    </Typography>
                    <Typography variant='body2' fontSize='14px' lineHeight='20px'>
                        {summaryName}
                    </Typography>
                    {tabChipLabel && (
                        <AxleChip
                            label={tabChipLabel}
                            thin
                            variant='outlined'
                            color={isFromIncompatible ? 'error' : 'primary'}
                        />
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button color='inherit' onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant='contained' color='primary' onClick={handleConfirm}>
                        Confirm
                    </Button>
                </Box>
            </DialogActions>
        </>
    );
};
